import {
  MODULATION_TECHNIQUES,
  SOURCE_KEY_POOL,
  buildModulationPlan,
  getValidDestinations,
} from '@tunasong/music-lib'
import type { ModulationTechnique, SourceKey } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import { useEffect, useState, type FC } from 'react'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import { useModulationAudition } from './modulation-lab-playback.js'
import { ModulationPhaseDiagram, type ModulationPhase } from './modulation-phase-diagram.js'
import styles from './modulation-explore-panel.module.css'

export interface ModulationExplorePanelProps {
  initialSource: SourceKey
  initialTechnique: ModulationTechnique
  initialDestination: string
}

/**
 * Modulation Explore panel — free-explore view with source / technique /
 * destination pickers, audition button, and live phase-highlight diagram.
 * The destination picker is disabled when technique='parallel' (the
 * destination is locked to the parallel minor of the source).
 *
 * Spec: docs/superpowers/specs/2026-05-26-modulation-lab-v1-design.md §6
 */
export const ModulationExplorePanel: FC<ModulationExplorePanelProps> = props => {
  const [source, setSource] = useState<SourceKey>(props.initialSource)
  const [technique, setTechnique] = useState<ModulationTechnique>(props.initialTechnique)
  const validDestinations = getValidDestinations(source, technique)
  const [destination, setDestination] = useState<string>(
    validDestinations.includes(props.initialDestination)
      ? props.initialDestination
      : validDestinations[0]
  )

  // When source or technique changes, snap destination to a valid value.
  useEffect(() => {
    if (!validDestinations.includes(destination)) {
      setDestination(validDestinations[0])
    }
  }, [validDestinations, destination])

  const plan = buildModulationPlan({ sourceKey: source, destinationKey: destination, technique })
  const { play, isPlaying, phaseBoundaries } = useModulationAudition({ plan })
  const [currentPhase, setCurrentPhase] = useState<ModulationPhase | null>(null)

  // Drive phase highlight from boundaries. setTimeouts are cancelled on
  // stop / unmount / re-play.
  useEffect(() => {
    if (!isPlaying || !phaseBoundaries) {
      setCurrentPhase(null)
      return
    }
    setCurrentPhase('source')
    const t1 = window.setTimeout(
      () => setCurrentPhase('modulation'),
      phaseBoundaries.modulationStartMs
    )
    const t2 = window.setTimeout(
      () => setCurrentPhase('destination'),
      phaseBoundaries.destinationStartMs
    )
    const t3 = window.setTimeout(() => setCurrentPhase(null), phaseBoundaries.endMs)
    return () => {
      window.clearTimeout(t1)
      window.clearTimeout(t2)
      window.clearTimeout(t3)
    }
  }, [isPlaying, phaseBoundaries])

  const fromLabel = useTheoryCopy('modulation-source-picker')
  const toLabel = useTheoryCopy('modulation-destination-picker')
  const techLabel = useTheoryCopy('modulation-technique-picker')

  return (
    <div className={styles.panel}>
      <div className={styles.pickers}>
        <label className={styles.pickerLabel}>
          <span>{fromLabel?.displayName ?? 'From'}</span>
          <select value={source} onChange={e => setSource(e.target.value as SourceKey)}>
            {SOURCE_KEY_POOL.map(k => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.pickerLabel}>
          <span>{techLabel?.displayName ?? 'Technique'}</span>
          <select
            value={technique}
            onChange={e => setTechnique(e.target.value as ModulationTechnique)}
          >
            {MODULATION_TECHNIQUES.map(t => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.pickerLabel}>
          <span>{toLabel?.displayName ?? 'To'}</span>
          <select
            value={destination}
            disabled={technique === 'parallel'}
            onChange={e => setDestination(e.target.value)}
          >
            {validDestinations.map(d => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </label>
      </div>
      <Button type="button" onClick={play} disabled={isPlaying}>
        {isPlaying ? 'Playing…' : 'Play modulation'}
      </Button>
      <ModulationPhaseDiagram plan={plan} currentPhase={currentPhase} />
    </div>
  )
}
